import { useCallback, useMemo } from 'react';

import {
  type ApolloQueryResult,
  type TypedDocumentNode,
  useApolloClient,
} from '@apollo/client';
import type { IServerSideGetRowsParams } from 'ag-grid-community';
import { format } from 'date-fns';

import { useFeatureFlag } from '@/app/services/features';
import {
  InputMaybe,
  ShipmentFilterInput,
  ShipmentSortInput,
  ShippingShipmentFragment,
} from '@/types.gql';

import { CONTENT_TYPE_FIELDS } from '../utils/columnDefs';

const OR_FLAG = 'shipping_or_content_filters';

export function useGraphQLDataSource<
  Result,
  Variables extends {
    skip: number;
    take?: InputMaybe<number>;
    order: InputMaybe<ShipmentSortInput | ShipmentSortInput[]>;
    where?: ShipmentFilterInput;
  },
>({
  query,
  queryName,
  variables,
  quickFilterText,
  selectedContentTypeFields,
}: {
  query: TypedDocumentNode<Result, Variables>;
  queryName: keyof Result;
  variables?: Variables;
  quickFilterText?: string;
  selectedContentTypeFields?: string[];
}) {
  const client = useApolloClient();
  const useOrFilters = useFeatureFlag(OR_FLAG);

  const buildNestedSortObject = (
    fieldPath: string,
    direction: 'ASC' | 'DESC',
  ): any => {
    const parts = fieldPath.split('.');
    if (parts.length === 1) {
      return { [fieldPath]: direction };
    }

    const result: any = {};
    let current = result;
    for (let i = 0; i < parts.length - 1; i++) {
      current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = direction;
    return result;
  };

  const getRows = useCallback(
    ({ request, success, fail, api }: IServerSideGetRowsParams) => {
      if (
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        return;
      }

      const order = request.sortModel
        .map((s) => ({
          sort: s.sort,
          field: api.getColumn(s.colId)?.getColDef().field,
        }))
        .filter((s) => s.field)
        .map((s) =>
          buildNestedSortObject(s.field!, s.sort === 'asc' ? 'ASC' : 'DESC'),
        );

      const filterConditions: any[] = [];

      if (useOrFilters) {
        const allSelected =
          !selectedContentTypeFields ||
          selectedContentTypeFields.length === 0 ||
          selectedContentTypeFields.length === CONTENT_TYPE_FIELDS.length;

        if (!allSelected) {
          filterConditions.push({
            or: selectedContentTypeFields!.map((f) => ({
              [f]: { eq: true },
            })),
          });
        }
      }

      Object.entries(request.filterModel ?? {}).forEach(
        ([colId, filterValue]) => {
          const field = api.getColumn(colId)?.getColDef().field;
          if (!field) return;

          if (
            CONTENT_TYPE_FIELDS.includes(field) &&
            filterValue.filterType === 'set'
          ) {
            if (useOrFilters) {
              return;
            }
            const booleanValues = filterValue.values.map(
              (value: string) => value === 'true',
            );
            if (booleanValues.length > 0) {
              filterConditions.push({
                or: booleanValues.map((value: boolean) => ({
                  [field]: { eq: value },
                })),
              });
            }

            return;
          }

          if (field === 'stateCode' && filterValue.filterType === 'set') {
            const stateValues = filterValue.values.map((value: string) =>
              value === '0' ? 0 : 1,
            );
            if (stateValues.length > 0) {
              filterConditions.push({
                or: stateValues.map((value: number) => ({
                  [field]: { eq: value },
                })),
              });
            }
            return;
          }

          if (field === 'shippingMethod' && filterValue.filterType === 'set') {
            const codes = filterValue.values
              .map((value: string) => Number(value))
              .filter(
                (code: number) => !Number.isNaN(code) && code >= 100000000,
              );
            if (codes.length > 0) {
              filterConditions.push({ [field]: { in: codes } });
            }
            return;
          }

          if (field === 'shipmentStatus' && filterValue.filterType === 'set') {
            const codes = filterValue.values
              .map((value: string) => Number(value))
              .filter(
                (code: number) => !Number.isNaN(code) && code >= 100000000,
              );
            if (codes.length > 0) {
              filterConditions.push({ [field]: { in: codes } });
            }
            return;
          }

          if (
            field === 'date' ||
            field === 'needByDate' ||
            field === 'createdOn'
          ) {
            const toLocalDate = (dateStr: string) =>
              format(new Date(dateStr), 'yyyy-MM-dd');

            if (filterValue.operator) {
              let dateRanges = [];
              if (filterValue.conditions) {
                dateRanges = filterValue.conditions.map(
                  (dateCondition: {
                    type: string;
                    dateFrom: string;
                    dateTo: string;
                  }) => {
                    if (dateCondition.type === 'inRange') {
                      return {
                        and: [
                          dateCondition.dateFrom && {
                            [field]: {
                              gte: toLocalDate(dateCondition.dateFrom),
                            },
                          },
                          dateCondition.dateTo && {
                            [field]: {
                              lte: toLocalDate(dateCondition.dateTo),
                            },
                          },
                        ].filter(Boolean),
                      };
                    }
                    return {
                      [field]: {
                        gt: toLocalDate(dateCondition.dateFrom),
                      },
                    };
                  },
                );
              } else if (filterValue.condition1) {
                dateRanges = [
                  filterValue.condition1.type === 'inRange'
                    ? {
                        and: [
                          filterValue.condition1.dateFrom && {
                            [field]: {
                              gte: toLocalDate(filterValue.condition1.dateFrom),
                            },
                          },
                          filterValue.condition1.dateTo && {
                            [field]: {
                              lte: toLocalDate(filterValue.condition1.dateTo),
                            },
                          },
                        ].filter(Boolean),
                      }
                    : {
                        [field]: {
                          gt: toLocalDate(filterValue.condition1.dateFrom),
                        },
                      },
                  filterValue.condition2.type === 'inRange'
                    ? {
                        and: [
                          filterValue.condition2.dateFrom && {
                            [field]: {
                              gte: toLocalDate(filterValue.condition2.dateFrom),
                            },
                          },
                          filterValue.condition2.dateTo && {
                            [field]: {
                              lte: toLocalDate(filterValue.condition2.dateTo),
                            },
                          },
                        ].filter(Boolean),
                      }
                    : {
                        [field]: {
                          gt: toLocalDate(filterValue.condition2.dateFrom),
                        },
                      },
                ];
              }

              filterConditions.push({
                [filterValue.operator.toLowerCase()]: dateRanges,
              });
            } else {
              if (filterValue.type === 'inRange') {
                filterConditions.push({
                  and: [
                    filterValue.dateFrom && {
                      [field]: {
                        gte: toLocalDate(filterValue.dateFrom),
                      },
                    },
                    filterValue.dateTo && {
                      [field]: {
                        lte: toLocalDate(filterValue.dateTo),
                      },
                    },
                  ].filter(Boolean),
                });
              } else if (filterValue.type === 'equals') {
                filterConditions.push({
                  [field]: { eq: toLocalDate(filterValue.dateFrom) },
                });
              } else {
                filterConditions.push({
                  and: [
                    filterValue.dateFrom && {
                      [field]: {
                        gt: toLocalDate(filterValue.dateFrom),
                      },
                    },
                    filterValue.dateTo && {
                      [field]: {
                        lt: toLocalDate(filterValue.dateTo),
                      },
                    },
                  ].filter(Boolean),
                });
              }
            }
            return;
          }

          const fieldParts = field.split('.');
          if (fieldParts.length > 1) {
            const [parent, child] = fieldParts;
            if (filterValue.type === 'contains') {
              filterConditions.push({
                [parent]: { [child]: { contains: filterValue.filter } },
              });
            } else if (filterValue.type === 'eq') {
              filterConditions.push({
                [parent]: { [child]: { eq: filterValue.filter } },
              });
            } else if (filterValue.type === 'neq') {
              filterConditions.push({
                [parent]: { [child]: { neq: filterValue.filter } },
              });
            } else if (filterValue.type === 'startsWith') {
              filterConditions.push({
                [parent]: { [child]: { startsWith: filterValue.filter } },
              });
            } else if (filterValue.type === 'endsWith') {
              filterConditions.push({
                [parent]: { [child]: { endsWith: filterValue.filter } },
              });
            }
            return;
          }

          // Handle regular fields
          if (filterValue.type === 'contains') {
            filterConditions.push({
              [field]: { contains: filterValue.filter },
            });
          } else if (filterValue.type === 'eq') {
            filterConditions.push({ [field]: { eq: filterValue.filter } });
          } else if (filterValue.type === 'neq') {
            filterConditions.push({ [field]: { neq: filterValue.filter } });
          } else if (filterValue.type === 'startsWith') {
            filterConditions.push({
              [field]: { startsWith: filterValue.filter },
            });
          } else if (filterValue.type === 'endsWith') {
            filterConditions.push({
              [field]: { endsWith: filterValue.filter },
            });
          } else if (filterValue.type === 'inRange') {
            if (filterValue.dateFrom) {
              filterConditions.push({ [field]: { gte: filterValue.dateFrom } });
            }
            if (filterValue.dateTo) {
              filterConditions.push({ [field]: { lte: filterValue.dateTo } });
            }
          } else if (filterValue.type === 'greaterThan') {
            filterConditions.push({ [field]: { gt: filterValue.filter } });
          } else if (filterValue.type === 'lessThan') {
            filterConditions.push({ [field]: { lt: filterValue.filter } });
          } else if (filterValue.type === 'greaterThanOrEqual') {
            filterConditions.push({ [field]: { gte: filterValue.filter } });
          } else if (filterValue.type === 'lessThanOrEqual') {
            filterConditions.push({ [field]: { lte: filterValue.filter } });
          } else if (filterValue.type === 'empty') {
            filterConditions.push({ [field]: { eq: null } });
          } else if (filterValue.type === 'notEmpty') {
            filterConditions.push({ [field]: { neq: null } });
          } else if (
            filterValue.type === 'equals' &&
            typeof filterValue.filter === 'boolean'
          ) {
            filterConditions.push({ [field]: { eq: filterValue.filter } });
          } else if (filterValue.filterType === 'set') {
            filterConditions.push({ [field]: { in: filterValue.values } });
          }
        },
      );

      const contentTypeFilters =
        !useOrFilters &&
        selectedContentTypeFields &&
        selectedContentTypeFields.length > 0
          ? [
              {
                or: selectedContentTypeFields.map((field) => ({
                  [field]: { eq: true },
                })),
              },
            ]
          : [];

      const search = {
        and: [
          ...filterConditions,
          quickFilterText
            ? {
                or: [
                  { shipmentNumber: { contains: quickFilterText } },
                  { tank: { contains: quickFilterText } },
                  { shipFromSite: { name: { contains: quickFilterText } } },
                  { comment: { contains: quickFilterText } },
                  { createdBy: { fullName: { contains: quickFilterText } } },
                  { shipName: { contains: quickFilterText } },
                  { shipState: { contains: quickFilterText } },
                  { trackingNumber: { contains: quickFilterText } },
                  { packedBy: { name: { contains: quickFilterText } } },
                  { shipFromSite: { name: { contains: quickFilterText } } },
                ],
              }
            : null,
          ...contentTypeFilters,
        ].filter(Boolean),
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const queryVariables = {
        ...(variables as Variables),
        skip: request.startRow,
        take: request.endRow - request.startRow,
        order: order.length > 0 ? order : undefined,
        where: search,
      };

      const executeQuery = (attempt: number): void => {
        client
          .query<Result, Variables>({
            query,
            variables: queryVariables,
            fetchPolicy: 'network-only',
            context: { fetchOptions: { signal: controller.signal } },
          })
          .then((res: ApolloQueryResult<Result>) => {
            clearTimeout(timeoutId);
            const queryResult = res.data[queryName] as {
              items: ShippingShipmentFragment[];
            };
            success({
              rowData: queryResult.items,
            });
          })
          .catch((error) => {
            if (attempt < 2) {
              console.warn(
                `Shipping grid query failed (attempt ${attempt + 1}/3), retrying...`,
                error,
              );
              setTimeout(() => executeQuery(attempt + 1), 1000 * (attempt + 1));
            } else {
              clearTimeout(timeoutId);
              console.error(
                'Shipping grid query failed after 3 attempts',
                error,
              );
              fail();
            }
          });
      };

      executeQuery(0);
    },
    [
      client,
      query,
      queryName,
      quickFilterText,
      selectedContentTypeFields,
      useOrFilters,
      variables,
    ],
  );

  return {
    dataSource: useMemo(() => ({ getRows }), [getRows]),
  };
}
